class CustomDropdown extends HTMLElement {
  constructor() {
    super();
    this.select = null;
    this.button = null;
    this.panel = null;
    this.optionsList = null;
    this.label = this.dataset.label || '';
    this.onDocClick = this.onDocClick.bind(this);
  }

  connectedCallback() {
    this.select = this.querySelector('select');
    if (!this.select) return;

    this.select.classList.add('custom-dropdown__native');
    this.build();
    this.syncFromSelect();
    this.bind();

    // Гарантуємо закритий стан після ініціалізації
    this.resetClosedState();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocClick);
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    // Очищуємо обробники подій опцій
    this.removeOptionEventListeners();
  }

  build() {
    this.classList.add('custom-dropdown--hydrated');
    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = 'custom-dropdown__button';
    this.button.setAttribute('aria-haspopup', 'listbox');
    this.button.setAttribute('aria-expanded', 'false');

    const labelSpan = document.createElement('span');
    labelSpan.className = 'custom-dropdown__label';
    labelSpan.textContent = this.label;
    const valueSpan = document.createElement('span');
    valueSpan.className = 'custom-dropdown__value';
    this.button.append(labelSpan, valueSpan);

    this.panel = document.createElement('div');
    this.panel.className = 'custom-dropdown__panel';
    this.panel.setAttribute('role', 'listbox');
    this.panel.hidden = true; // Закриваємо панель по дефолту
    this.panel.style.zIndex = '9999'; // Встановлюємо високий z-index

    this.optionsList = document.createElement('ul');
    this.optionsList.className = 'custom-dropdown__options';
    this.panel.appendChild(this.optionsList);

    this.append(this.button, this.panel);
  }

  bind() {
    this.button.addEventListener('click', () => this.toggle());
    this.select.addEventListener('change', () => this.syncFromSelect());
    this.select.addEventListener('input', () => this.syncFromSelect());
    document.addEventListener('click', this.onDocClick);
    
    // Синхронізація при зміні атрибутів опцій ззовні
    const observer = new MutationObserver(() => this.syncFromSelect());
    observer.observe(this.select, { 
      childList: true, 
      subtree: true, 
      attributes: true, 
      attributeFilter: ['selected', 'disabled'] 
    });
    this.mutationObserver = observer;
  }

  onDocClick(e) {
    // Перевіряємо чи клік був поза компонентом
    if (!this.contains(e.target)) {
      this.close();
    }
  }

  toggle() {
    const open = this.button.getAttribute('aria-expanded') === 'true';
    open ? this.close() : this.open();
  }

  open() {
    this.button.setAttribute('aria-expanded', 'true');
    this.panel.hidden = false;
    // Додаємо клас відкриття для керування макетом через CSS
    this.classList.add('is-open');
    const item = this.closest('.designed-with__item');
    if (item) item.classList.add('dropdown-open');
    // Підстраховка для старих стилів накладання
    this.panel.style.zIndex = '9999';

    // Розмістити панель поверх та вирівняти ширину з кнопкою
    const rect = this.button.getBoundingClientRect();
    this.panel.style.minWidth = rect.width + 'px';

    // Глобальний стан: коли будь-який dropdown відкритий в Designed With
    // опускаємо секцію Our Looks нижче за допомогою CSS (через клас на body)
    if (!document.body.classList.contains('designed-dropdown-open')) {
      document.body.classList.add('designed-dropdown-open');
    }
  }

  close() {
    this.button.setAttribute('aria-expanded', 'false');
    this.panel.hidden = true;
    // Приховуємо класи стану
    this.classList.remove('is-open');
    const item = this.closest('.designed-with__item');
    if (item) item.classList.remove('dropdown-open');

    // Якщо більше немає відкритих дропдаунів — прибираємо глобальний клас
    queueMicrotask(() => {
      const anyOpen = document.querySelector('custom-dropdown.is-open');
      if (!anyOpen) {
        document.body.classList.remove('designed-dropdown-open');
      }
    });
  }

  // Скидання стану на закритий (викликається після ініціалізації)
  resetClosedState() {
    try {
      this.classList.remove('is-open');
      this.button?.setAttribute('aria-expanded', 'false');
      if (this.panel) this.panel.hidden = true;
      const item = this.closest('.designed-with__item');
      if (item) item.classList.remove('dropdown-open');

      // Перестраховка: якщо інші інстанси не відкриті — знімаємо глобальний клас
      const anyOpen = document.querySelector('custom-dropdown.is-open');
      if (!anyOpen) document.body.classList.remove('designed-dropdown-open');
    } catch (e) {
      // no-op
    }
  }

  syncFromSelect() {
    const current = this.select.selectedOptions[0];
    if (current) {
      const valueElement = this.button.querySelector('.custom-dropdown__value');
      if (valueElement) {
        valueElement.textContent = current.textContent;
      }
    }

    // Очищуємо події перед перестворенням опцій
    this.removeOptionEventListeners();
    this.optionsList.innerHTML = '';
    
    Array.from(this.select.options).forEach((opt) => {
      const li = document.createElement('li');
      li.className = 'custom-dropdown__option';
      li.setAttribute('role', 'option');
      li.dataset.value = opt.value;
      
      // Перевіряємо як disabled так і data-available
      const isDisabled = opt.disabled || opt.dataset.available === 'false';
      if (isDisabled) li.classList.add('is-disabled');
      if (opt.selected) li.classList.add('is-selected');
      
      li.innerHTML = `<span>${opt.textContent}</span>`;
      
      // Зберігаємо обробник для можливості його видалення
      const clickHandler = (e) => this.handleOptionClick(e, opt, li);
      li.clickHandler = clickHandler;
      li.addEventListener('click', clickHandler);
      
      this.optionsList.appendChild(li);
    });
  }

  removeOptionEventListeners() {
    // Видаляємо всі попередні обробники подій
    if (!this.optionsList) return;
    
    const options = this.optionsList.querySelectorAll('.custom-dropdown__option');
    options.forEach(option => {
      if (option.clickHandler) {
        option.removeEventListener('click', option.clickHandler);
        delete option.clickHandler;
      }
    });
  }

  handleOptionClick(e, opt, li) {
    e.preventDefault();
    e.stopPropagation();
    
    // Перевіряємо чи опція недоступна
    const isDisabled = opt.disabled || opt.dataset.available === 'false';
    if (isDisabled) {
      console.log('Option is disabled, ignoring click');
      return;
    }
    
    try {
      console.log('Selecting option:', opt.value, opt.textContent);
      
      // Оновлюємо стани опцій
      Array.from(this.select.options).forEach((o) => {
        const isSel = o.value === opt.value;
        o.selected = isSel;
        if (isSel) {
          o.setAttribute('selected', 'selected');
        } else {
          o.removeAttribute('selected');
        }
      });
      
      // Оновлюємо значення select
      this.select.value = opt.value;
      
      // Відправляємо події з затримкою для кращої сумісності
      setTimeout(() => {
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
        this.select.dispatchEvent(new Event('input', { bubbles: true }));
      }, 0);
      
      // Оновлюємо відображення кнопки
      const valueElement = this.button.querySelector('.custom-dropdown__value');
      if (valueElement) {
        valueElement.textContent = opt.textContent;
      }
      
      // Оновлюємо стани опцій в дропдауні
      this.updateOptionStates();
      
      // Закриваємо дропдаун
      this.close();
      
    } catch (error) {
      console.error('Error handling option click:', error);
    }
  }

  updateOptionStates() {
    // Оновлення станів опцій після зміни варіанту
    const options = this.optionsList.querySelectorAll('.custom-dropdown__option');
    options.forEach((optionElement) => {
      const value = optionElement.dataset.value;
      const selectOption = this.select.querySelector(`option[value="${value}"]`);
      
      if (selectOption) {
        // Оновлення disabled стану (перевіряємо як disabled так і data-available)
        const isDisabled = selectOption.disabled || selectOption.dataset.available === 'false';
        if (isDisabled) {
          optionElement.classList.add('is-disabled');
        } else {
          optionElement.classList.remove('is-disabled');
        }
        
        // Оновлення selected стану
        if (selectOption.selected) {
          optionElement.classList.add('is-selected');
          // Видаляємо selected клас з інших опцій
          options.forEach(other => {
            if (other !== optionElement) {
              other.classList.remove('is-selected');
            }
          });
        } else {
          optionElement.classList.remove('is-selected');
        }
      }
    });
  }
}

customElements.define('custom-dropdown', CustomDropdown);


